import { Stack } from '@mantine/core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EntityType } from '../../../../api/common/types';
import { useAvailableTaskPoints } from '../../../../api/task-points-client/task-points-client.hooks';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { useProcessAnalysisStore } from '../../../../store/process-analysis-store/process-analysis-store';
import { setSelectedPoints } from '../../../../store/process-analysis-store/process-analysis-store.actions';
import { Select } from '../../../../ui-library/select/select';
import { Text } from '../../../../ui-library/typography/typography';

const ALL_POINTS_VALUE = '__all__';
// Wire sentinel (shared with the backend TaskGroup) meaning "tasks with no
// estimate" — points IS NULL — as distinct from "All story points" (no filter).
const NO_POINTS_VALUE = '__none__';

/**
 * Agents-tab Cycle Time filter that narrows the task-level metrics by story
 * points. Numeric options come from the point values actually present in the
 * date-scoped data; a "No points" option is offered only when unestimated
 * tasks exist, and "All story points" applies no filter at all. The control is
 * disabled only when the scope has neither point values nor unestimated tasks.
 * Renders the shared `ui-library` Select so it matches the sibling Allocation
 * Type and Task Type filters.
 *
 * @returns {JSX.Element} Label + Select control
 */
export const PointsFilter = () => {
  const { t } = useTranslation();
  const organizationId = useGlobalStore((state) => state.organization?.id ?? null);
  const portfolioId = useGlobalStore((state) => state.portfolio?.id ?? null);
  const selectedPoints = useProcessAnalysisStore((state) => state.selectedPoints);
  const startDate = useProcessAnalysisStore((state) => state.startDate);
  const endDate = useProcessAnalysisStore((state) => state.endDate);
  const { points, hasUnestimated } = useAvailableTaskPoints({
    organizationId,
    entityType: EntityType.Portfolio,
    entityId: portfolioId,
    startDate,
    endDate,
  });

  const allPointsLabel = t('process_analysis.points_filter.all');
  const noPointsLabel = t('process_analysis.points_filter.none');

  // Nothing to filter by when there are neither point values nor unestimated tasks.
  const hasOptions = points.length > 0 || hasUnestimated;

  const options = useMemo(
    () => [
      { value: ALL_POINTS_VALUE, label: allPointsLabel },
      ...(hasUnestimated ? [{ value: NO_POINTS_VALUE, label: noPointsLabel }] : []),
      ...points.map((value) => ({ value: String(value), label: String(value) })),
    ],
    [allPointsLabel, noPointsLabel, hasUnestimated, points],
  );

  const handleChange = (value: string) => {
    if (!value || value === ALL_POINTS_VALUE) {
      setSelectedPoints(null);
      return;
    }
    if (value === NO_POINTS_VALUE) {
      setSelectedPoints(NO_POINTS_VALUE);
      return;
    }
    setSelectedPoints(Number(value));
  };

  return (
    <Stack gap={8}>
      <Text size="small" weight="bold">
        {t('process_analysis.filters.points')}
      </Text>
      <Select
        options={options}
        value={selectedPoints != null ? String(selectedPoints) : ALL_POINTS_VALUE}
        onChange={handleChange}
        size="sm"
        disabled={!hasOptions}
      />
    </Stack>
  );
};
